import React, { useState, useEffect, useRef } from 'react';
import Modal from '../common/Modal';
import { Template } from '../../types/template';
import { Contract } from '../../types/contract';
import { ExclamationTriangleIcon, ArrowDownTrayIcon, EyeIcon, ArrowPathIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { renderAsync } from 'docx-preview';

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  template?: Template | null;
  contract?: Contract | null;
}

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  isOpen,
  onClose,
  template,
  contract
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const { t } = useLanguage();
  
  // Referenciák a dokumentum megjelenítéshez
  const docxContainerRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLObjectElement>(null);

  // Eldöntjük, melyik dokumentumot használjuk: template vagy contract
  const currentDocument = template || contract;

  // Fájltípus felismerése a fájlnév alapján
  const getFileType = (url: string, fileName: string): string => {
    // Először a fájlnévből próbáljuk kinyerni a kiterjesztést
    const fileNameExt = fileName?.split('.').pop()?.toLowerCase();
    if (fileNameExt) {
      return fileNameExt;
    }
    
    // Ha nincs fájlnév, akkor az URL-ből próbáljuk kinyerni
    // Firebase Storage URL-ben az alt=media előtt van a fájlnév
    const filePathMatch = url.match(/\/([^/?]+)\?/);
    if (filePathMatch && filePathMatch[1]) {
      const decodedFileName = decodeURIComponent(filePathMatch[1]);
      const ext = decodedFileName.split('.').pop()?.toLowerCase();
      if (ext) {
        return ext;
      }
    }
    
    // Végső megoldásként az URL végét nézzük
    return url.split('.').pop()?.toLowerCase() || 'unknown';
  };

  // DOCX dokumentumok megjelenítése
  const renderDocx = async (fileUrl: string) => {
    if (!docxContainerRef.current) {
      console.error('docxContainerRef.current nem létezik');
      setError(t("contracts.documentPreviewModal.documentDisplayError"));
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Fájl letöltése
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`HTTP hiba: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Ellenőrizzük újra, hogy a konténer még mindig létezik-e
      if (!docxContainerRef.current) {
        throw new Error('A dokumentum konténer nem elérhető');
      }
      
      // Dokumentum megjelenítése docx-preview segítségével
      await renderAsync(blob, docxContainerRef.current, undefined, {
        className: 'docx-viewer',
        inWrapper: true,
        breakPages: true,                   // Oldalak tördelése engedélyezve
        ignoreLastRenderedPageBreak: true,  // Word oldalhatárok figyelmen kívül hagyása
        ignoreWidth: false,                 // Oldalszélesség megtartása
        ignoreHeight: false,                // Oldalmagasság megtartása
        ignoreFonts: false,                 // Betűtípusok megtartása
        hideWrapperOnPrint: true,           // Nyomtatásnál eltűnik a wrapper
        experimental: false,                // Kísérleti funkciók kikapcsolva
        trimXmlDeclaration: true,           // XML deklaráció eltávolítása
        renderHeaders: true,                // Fejlécek megjelenítése
        renderFooters: true,                // Láblécek megjelenítése
        renderFootnotes: true,              // Lábjegyzetek megjelenítése
        renderEndnotes: true,               // Végjegyzetek megjelenítése
        renderChanges: false,               // Változáskövetés kikapcsolva
        renderComments: false,              // Megjegyzések kikapcsolva
        renderAltChunks: true,              // Alternatív tartalom megjelenítése
        debug: process.env.NODE_ENV === 'development' // Debug üzenetek csak fejlesztői módban
      });
      
      setIsLoading(false);
    } catch (err: any) {
      console.error('Hiba a dokumentum megjelenítése során:', err);
      setError(err.message || t("contracts.documentPreviewModal.documentDisplayError"));
      setIsLoading(false);
    }
  };

  // Dokumentum újratöltése
  const handleReload = () => {
    if (!currentDocument?.fileUrl) return;
    
    const fileType = getFileType(currentDocument.fileUrl, currentDocument.fileName || '');
    
    if (fileType === 'docx') {
      if (docxContainerRef.current) {
        try {
          docxContainerRef.current.innerHTML = '';
          renderDocx(currentDocument.fileUrl);
        } catch (err) {
          console.error('Hiba a dokumentum újratöltése során:', err);
          setError(t("contracts.documentPreviewModal.documentDisplayError"));
        }
      }
    } else if (fileType === 'pdf') {
      setIsLoading(true);
      if (pdfContainerRef.current) {
        const src = pdfContainerRef.current.getAttribute('data');
        if (src) {
          pdfContainerRef.current.setAttribute('data', '');
          setTimeout(() => {
            pdfContainerRef.current?.setAttribute('data', src);
            setIsLoading(false);
          }, 100);
        }
      }
    } else {
      // Egyéb fájltípusok esetén nincs mit újratölteni, csak a letöltési link áll rendelkezésre
      setIsLoading(false);
    }
  };

  // Indítjuk a betöltést, amikor a modal kinyílik
  useEffect(() => {
    if (isOpen && currentDocument?.fileUrl) {
      const renderTimer = setTimeout(() => {
        const fileType = getFileType(currentDocument.fileUrl as string, currentDocument.fileName || '');
        
        if (fileType === 'docx') {
          renderDocx(currentDocument.fileUrl as string);
        } else {
          setIsLoading(false);
        }
      }, 100); // Rövid késleltetés
      
      return () => clearTimeout(renderTimer);
    }
  }, [isOpen, currentDocument]);

  if (!currentDocument) return null;

  // Ha nincs fileUrl, akkor hibaüzenetet jelenítünk meg
  if (!currentDocument.fileUrl) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t("contracts.documentPreviewModal.title")}
        size="lg"
      >
        <div className="p-8 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">
            {t("contracts.documentPreviewModal.documentNotDisplayable")}
          </h3>
          <p className="text-gray-600">
            {t("contracts.documentPreviewModal.documentNotFound")}
          </p>
        </div>
      </Modal>
    );
  }

  const renderContent = () => {
    // Mivel már ellenőriztük fentebb a fileUrl létezését, itt biztosan string
    const fileUrl = currentDocument.fileUrl as string;
    const fileType = getFileType(fileUrl, currentDocument.fileName || '');

    // Meghatározzuk a megjelenítendő nevet és egyéb adatokat
    const documentName = currentDocument.name || '';
    const description = 'templateName' in currentDocument ? currentDocument.templateName :
      'description' in currentDocument ? currentDocument.description : '';

    return (
      <div className="p-4 space-y-4">
        {/* Debug információk és műveletek */}
        <div className="bg-white rounded-lg p-4 space-y-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-800">{t("contracts.documentPreviewModal.documentInformation")}</h3>
            <button
              onClick={() => setShowDebug(!showDebug)}
              className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              {showDebug ? t("contracts.documentPreviewModal.hideDetails") : t("contracts.documentPreviewModal.showDetails")}
            </button>
          </div>

          {showDebug && (
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-600">Fájlnév: </span>
                <span className="text-gray-800">{currentDocument.fileName}</span>
              </div>
              <div>
                <span className="text-gray-600">Név: </span>
                <span className="text-gray-800">{documentName}</span>
              </div>
              {description && (
                <div>
                  <span className="text-gray-600">Leírás: </span>
                  <span className="text-gray-800">{description}</span>
                </div>
              )}
              <div>
                <span className="text-gray-600">Firebase URL: </span>
                <span className="text-gray-800 break-all">{fileUrl}</span>
              </div>
              <div>
                <span className="text-gray-600">Fájl típusa: </span>
                <span className="text-gray-800">{fileType}</span>
              </div>
            </div>
          )}

          <div className="flex space-x-4">
            <a
              href={fileUrl}
              download={currentDocument.fileName || 'document'}
              className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              {t("contracts.documentPreviewModal.download")}
            </a>
            <button
              onClick={handleReload}
              className="flex items-center px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              {t("contracts.documentPreviewModal.reload")}
            </button>
          </div>
        </div>

        {/* Dokumentum előnézet */}
        <div className="h-[60vh] relative bg-white rounded-lg overflow-hidden border border-gray-200">
          {/* Betöltési állapot jelzése */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100/50 z-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          )}
          
          {/* Hibaüzenet megjelenítése */}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20">
              <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mb-4" />
              <h2 className="text-lg font-medium text-gray-800 mb-2">{t("contracts.documentPreviewModal.documentLoadError")}</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <div className="flex space-x-4">
                <button 
                  onClick={handleReload} 
                  className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  {t("common.reload")}
                </button>
                <a
                  href={fileUrl}
                  download={currentDocument.fileName || "document"}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors inline-flex items-center"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  {t("contracts.documentPreviewModal.download")}
                </a>
              </div>
            </div>
          )}
          
          {/* DOCX megjelenítő */}
          {fileType === 'docx' && (
            <div 
              ref={docxContainerRef} 
              className="w-full h-full bg-white overflow-auto"
              id="docx-container-modal"
            ></div>
          )}
          
          {/* PDF megjelenítő */}
          {fileType === 'pdf' && (
            <object
              ref={pdfContainerRef}
              data={fileUrl}
              type="application/pdf"
              className="w-full h-full" 
            >
              <p className="text-center p-4 text-gray-800">
                {t("contracts.browserCannotDisplay")}
              </p>
            </object>
          )}
          
          {/* Nem támogatott fájlformátum - csak letöltési lehetőség */}
          {fileType !== 'docx' && fileType !== 'pdf' && (
            <div className="w-full h-full flex flex-col items-center justify-center bg-white">
              <DocumentArrowDownIcon className="h-16 w-16 text-gray-500 mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                {t("contracts.unsupportedFileFormat")}
              </h2>
              <p className="text-gray-600 mb-6 text-center max-w-md">
                {t("contracts.cannotPreviewThisFormat")}
              </p>
              <a
                href={fileUrl}
                download={currentDocument.fileName || "document"}
                className="px-6 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors inline-flex items-center text-lg"
              >
                <ArrowDownTrayIcon className="h-6 w-6 mr-2" />
                {t("contracts.documentPreviewModal.download")}
              </a>
              <p className="text-gray-500 mt-4 text-sm">
                {currentDocument.fileName}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setError(null);
        setIsLoading(false);
        onClose();
      }}
      title={t("contracts.documentPreviewModal.title")}
      size="4xl"
    >
      {renderContent()}
    </Modal>
  );
};

export default DocumentPreviewModal; 